import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "../api/publicClient";
import FileDropzone from "../components/FileDropzone";

const colors = {
  blue900: "#0f2d63", blue700: "#1d4ed8", blue600: "#2563eb",
  blue100: "#e6edfb", blue50: "#f3f6fd",
  green700: "#15803d", green600: "#16a34a", green100: "#dcfce7",
  amber700: "#b45309", amber100: "#fef3c7",
  red700: "#b91c1c", red100: "#fbe6e6", red50: "#fdf3f3",
  ink900: "#10151f", ink600: "#52596b", ink400: "#8892a6",
  line: "#e6e9f0", white: "#ffffff", grey100: "#f1f3f8",
};

function naira(n) {
  return "\u20a6" + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const styles = {
  body: { margin: 0, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: colors.grey100, color: colors.ink900, minHeight: "100vh" },
  topbar: { background: colors.blue900, color: colors.white, padding: "16px 24px", display: "flex", alignItems: "center", gap: 10, fontWeight: 700 },
  topbarImg: { width: 24, height: 24, objectFit: "contain", borderRadius: 6 },
  layout: { maxWidth: 720, margin: "0 auto", padding: "28px 20px 60px" },
  panel: { background: colors.white, border: `1px solid ${colors.line}`, borderRadius: 18, padding: 22, marginBottom: 18, boxShadow: "0 1px 2px rgba(16,21,31,0.04), 0 8px 24px rgba(16,21,31,0.05)" },
  welcome: { fontSize: 24, fontWeight: 800 },
  heroLabel: { fontSize: 12, color: colors.ink400, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", marginTop: 16 },
  heroValue: { fontSize: 34, fontWeight: 800, color: colors.red700, marginTop: 2, letterSpacing: "-0.01em" },
  heroSub: { display: "flex", gap: 20, marginTop: 8, fontSize: 12.5, color: colors.ink400, flexWrap: "wrap" },
  heroSubStrong: { color: colors.ink600 },
  bankBox: { background: colors.blue50, border: `1px solid ${colors.blue100}`, borderRadius: 12, padding: 16, marginTop: 18 },
  bankRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: 13.5 },
  bankLabel: { color: colors.ink400 },
  acctCopy: { display: "flex", alignItems: "center", gap: 10 },
  copyBtn: { border: `1px solid ${colors.blue100}`, background: colors.white, color: colors.blue700, fontWeight: 700, fontSize: 12, padding: "5px 10px", borderRadius: 7, cursor: "pointer" },
  sectionTitle: { fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: colors.ink400, margin: "4px 0 12px" },
  planCard: { background: colors.white, border: `1px solid ${colors.line}`, borderRadius: 18, padding: 20, marginBottom: 14, boxShadow: "0 1px 2px rgba(16,21,31,0.04), 0 8px 24px rgba(16,21,31,0.05)" },
  planCardTop: { display: "flex", alignItems: "center", gap: 14, marginBottom: 14 },
  planIcon: { width: 44, height: 44, borderRadius: 12, background: colors.blue100, color: colors.blue700, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, flexShrink: 0 },
  planName: { fontWeight: 800, fontSize: 16 },
  planProgressText: { fontSize: 12, color: colors.ink400, marginTop: 2 },
  progressTrack: { background: colors.grey100, borderRadius: 999, height: 7, overflow: "hidden", marginBottom: 16 },
  progressFill: { height: "100%", borderRadius: 999, background: colors.blue600 },
  paymentHistory: { marginBottom: 16, maxHeight: 160, overflowY: "auto", border: `1px solid ${colors.line}`, borderRadius: 10 },
  phLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", color: colors.ink400, padding: "8px 12px 4px" },
  phRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", borderTop: `1px solid ${colors.line}` },
  phRowFirst: { borderTop: "none" },
  phAmount: { fontWeight: 700, fontSize: 13 },
  phDate: { fontSize: 11, color: colors.ink400, marginLeft: 6 },
  phStatus: { fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" },
  planFigures: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 },
  figureLabel: { fontSize: 11.5, color: colors.ink400, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" },
  figureValue: { fontSize: 19, fontWeight: 800, color: colors.red700, marginTop: 2 },
  payBtn: { background: colors.blue600, color: colors.white, border: "none", borderRadius: 12, padding: "13px 0", fontWeight: 800, fontSize: 14, cursor: "pointer", width: "100%" },
  payBtnDone: { background: colors.green600, cursor: "default" },
  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(15, 45, 99, 0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 },
  modal: { background: colors.white, borderRadius: 16, padding: 0, width: "100%", maxWidth: 380, maxHeight: "90vh", overflowY: "auto" },
  modalInner: { padding: 22 },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  modalTitle: { fontWeight: 800, fontSize: 16 },
  modalClose: { border: "none", background: "transparent", fontSize: 18, color: colors.ink400, cursor: "pointer", lineHeight: 1 },
  receiptBox: { marginTop: 16 },
  receiptLabel: { display: "block", fontSize: 12.5, fontWeight: 700, color: colors.ink600, marginBottom: 6 },
  receiptStatus: { fontSize: 12, marginTop: 6, minHeight: 16 },
  formLabel: { display: "block", fontSize: 12.5, fontWeight: 700, color: colors.ink600, marginBottom: 6, marginTop: 16 },
  formInput: { width: "100%", border: `1px solid ${colors.line}`, borderRadius: 10, padding: "11px 13px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" },
  submitBtn: { background: colors.blue600, color: colors.white, border: "none", borderRadius: 10, padding: "11px 18px", fontWeight: 800, fontSize: 13.5, cursor: "pointer", width: "100%" },
  submitBtnDisabled: { background: colors.ink400, cursor: "not-allowed" },
  amountError: { color: colors.red700, fontSize: 12, marginTop: -6, marginBottom: 10 },
  messages: { maxWidth: 720, margin: "16px auto 0", padding: "0 20px" },
  msg: { borderRadius: 10, padding: "10px 14px", fontSize: 13.5, marginBottom: 8 },
  msgSuccess: { background: colors.blue50, color: colors.blue700, border: `1px solid ${colors.blue100}` },
  msgError: { background: colors.red50, color: colors.red700, border: `1px solid ${colors.red100}` },
  empty: { textAlign: "center", color: colors.ink400, padding: 16, fontSize: 13 },
};

const statusColors = {
  confirmed: { background: colors.green100, color: colors.green700 },
  pending: { background: colors.amber100, color: colors.amber700 },
  rejected: { background: colors.red100, color: colors.red700 },
};

export default function CustomerPublicShort() {
  const { shortCode } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [payingPlan, setPayingPlan] = useState(null);

  const load = useCallback(() => {
    publicApi
      .getCustomerByShortCode(shortCode)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [shortCode]);

  useEffect(() => {
    load();
  }, [load]);

  function copyAccount() {
    const text = data.account_number;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  }

  function fallbackCopy(text) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand("copy");
    } catch {
      // nothing more we can do silently
    }
    document.body.removeChild(ta);
  }

  function handlePaid() {
    setPayingPlan(null);
    setMessage({
      type: "success",
      text: "Thanks — we've recorded your payment claim. It will show as 'pending' until our accountant confirms it against the bank statement.",
    });
    load();
  }

  if (error) {
    return (
      <div style={styles.body}>
        <div style={styles.topbar}>
          <img src="/otic-logo.png" alt="Otic" style={styles.topbarImg} />
          Payment Plans
        </div>
        <div style={styles.layout}>
          <div style={styles.empty}>{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.body}>
        <div style={styles.topbar}>
          <img src="/otic-logo.png" alt="Otic" style={styles.topbarImg} />
          Payment Plans
        </div>
        <div style={styles.layout}>
          <div style={styles.empty}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.body}>
      <div style={styles.topbar}>
        <img src="/otic-logo.png" alt="Otic" style={styles.topbarImg} />
        Payment Plans
      </div>

      {message && (
        <div style={styles.messages}>
          <div style={{ ...styles.msg, ...(message.type === "success" ? styles.msgSuccess : styles.msgError) }}>
            {message.text}
          </div>
        </div>
      )}

      <div style={styles.layout}>
        <div style={styles.panel}>
          <div style={styles.welcome}>Welcome back, {data.name} </div>

          <div style={styles.heroLabel}>Outstanding Balance</div>
          <div style={styles.heroValue}>{naira(data.total_balance)}</div>
          <div style={styles.heroSub}>
            <span>Total <strong style={styles.heroSubStrong}>{naira(data.total_price)}</strong></span>
            <span>Paid <strong style={styles.heroSubStrong}>{naira(data.total_paid)}</strong></span>
            <span>{data.plans.length} plan{data.plans.length === 1 ? "" : "s"}</span>
          </div>

          <div style={styles.bankBox}>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Bank</span>
              <span>{data.bank_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account name</span>
              <span>{data.account_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account number</span>
              <span style={styles.acctCopy}>
                <span>{data.account_number}</span>
                <button type="button" style={styles.copyBtn} onClick={copyAccount}>Copy</button>
              </span>
            </div>
          </div>
        </div>

        <div style={styles.sectionTitle}>Your plans</div>

        {data.plans.length === 0 && <div style={styles.empty}>No plans yet.</div>}

        {data.plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} onPay={() => setPayingPlan(plan)} />
        ))}
      </div>

      {payingPlan && (
        <PayModal
          plan={payingPlan}
          token={data.token}
          bankInfo={data}
          onClose={() => setPayingPlan(null)}
          onPaid={handlePaid}
        />
      )}
    </div>
  );
}

function PlanCard({ plan, onPay }) {
  return (
    <div style={styles.planCard}>
      <div style={styles.planCardTop}>
        <div style={styles.planIcon}>{plan.equipment_name.slice(0, 1)}</div>
        <div>
          <div style={styles.planName}>{plan.equipment_name}</div>
          <div style={styles.planProgressText}>{plan.percent_done}% paid</div>
        </div>
      </div>

      <div style={styles.progressTrack}>
        <div style={{ ...styles.progressFill, width: `${plan.percent_done}%` }} />
      </div>

      {plan.payments.length > 0 && (
        <div style={styles.paymentHistory}>
          <div style={styles.phLabel}>Payment history</div>
          {plan.payments.map((p, i) => (
            <div key={p.id} style={{ ...styles.phRow, ...(i === 0 ? styles.phRowFirst : {}) }}>
              <div>
                <span style={styles.phAmount}>{naira(p.amount)}</span>
                <span style={styles.phDate}>
                  {p.submitted_at ? new Date(p.submitted_at).toLocaleDateString("en-NG", { month: "short", day: "numeric", year: "numeric" }) : ""}
                </span>
              </div>
              <span style={{ ...styles.phStatus, ...(statusColors[p.status] || {}) }}>
                {p.status === "confirmed" ? "Confirmed" : p.status === "rejected" ? "Rejected" : "Pending"}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={styles.planFigures}>
        <div>
          <div style={styles.figureLabel}>Balance</div>
          <div style={styles.figureValue}>{naira(plan.balance)}</div>
        </div>
      </div>

      {plan.is_completed ? (
        <button type="button" disabled style={{ ...styles.payBtn, ...styles.payBtnDone }}>
          Fully Paid ✓
        </button>
      ) : (
        <button type="button" style={styles.payBtn} onClick={onPay}>
          Pay Now
        </button>
      )}
    </div>
  );
}

function PayModal({ plan, token, bankInfo, onClose, onPaid }) {
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState("");
  const [amountLocked, setAmountLocked] = useState(true);
  const [receiptStatus, setReceiptStatus] = useState("");
  const [receiptStatusColor, setReceiptStatusColor] = useState(colors.ink400);
  const [amountError, setAmountError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const balance = Number(plan.balance);

  function formatAmountInput(raw) {
    let digits = raw.replace(/[^\d.]/g, "");
    const parts = digits.split(".");
    const intPart = parts[0].replace(/^0+(?=\d)/, "");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? withCommas + "." + parts[1] : withCommas;
  }

  function validateAmount(value) {
    const entered = parseFloat(value.replace(/,/g, "")) || 0;
    if (entered > balance) {
      setAmountError(`That's more than your remaining balance (${naira(balance)}).`);
      return false;
    }
    setAmountError("");
    return true;
  }

  function handleAmountChange(e) {
    const formatted = formatAmountInput(e.target.value);
    setAmount(formatted);
    validateAmount(formatted);
  }

  async function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setReceiptStatus("Reading receipt...");
    setReceiptStatusColor(colors.ink400);

    const fd = new FormData();
    fd.append("receipt", f);
    try {
      const result = await publicApi.analyzeReceipt(token, fd);
      if (result.amount) {
        const formatted = formatAmountInput(String(result.amount));
        setAmount(formatted);
        validateAmount(formatted);
        setReceiptStatus("Filled in from your receipt — please double check it before submitting.");
        setReceiptStatusColor(colors.blue700);
      } else {
        setReceiptStatus("Couldn't read an amount from that file — please enter it manually.");
        setReceiptStatusColor(colors.red700);
      }
    } catch {
      setReceiptStatus("Something went wrong — please enter the amount manually.");
      setReceiptStatusColor(colors.red700);
    } finally {
      setAmountLocked(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validateAmount(amount)) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("plan_id", plan.id);
      fd.append("amount", amount);
      if (file) fd.append("receipt", file);
      await publicApi.submitPayment(token, fd);
      onPaid();
    } catch (err) {
      setSubmitError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !!file && !amountLocked && !amountError && amount;

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalInner}>
          <div style={styles.modalHead}>
            <div style={styles.modalTitle}>Pay towards {plan.equipment_name}</div>
            <button type="button" style={styles.modalClose} onClick={onClose}>✕</button>
          </div>

          <div style={{ ...styles.bankBox, marginTop: 0 }}>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Bank</span>
              <span>{bankInfo.bank_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account name</span>
              <span>{bankInfo.account_name}</span>
            </div>
            <div style={styles.bankRow}>
              <span style={styles.bankLabel}>Account number</span>
              <span>{bankInfo.account_number}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={styles.receiptBox}>
              <label style={styles.receiptLabel}>Upload receipt (required)</label>
              <FileDropzone
                accept="image/*,application/pdf"
                required
                onChange={handleFileChange}
                title="Tap to upload your receipt"
                hint="A photo, screenshot or PDF of your transfer"
              />
              {receiptStatus && (
                <div style={{ ...styles.receiptStatus, color: receiptStatusColor }}>{receiptStatus}</div>
              )}
            </div>

            <label style={styles.formLabel}>Amount sent (₦)</label>
            <input
              type="text"
              inputMode="decimal"
              required
              readOnly={amountLocked}
              placeholder={amountLocked ? "Upload a receipt first" : "e.g. 400,000"}
              value={amount}
              onChange={handleAmountChange}
              style={styles.formInput}
            />
            {amountError && <div style={styles.amountError}>{amountError}</div>}
            {submitError && <div style={styles.amountError}>{submitError}</div>}

            <button
              type="submit"
              style={{ ...styles.submitBtn, ...((!canSubmit || submitting) ? styles.submitBtnDisabled : {}) }}
              disabled={!canSubmit || submitting}
            >
              {submitting
                ? "Sending..."
                : amountLocked
                ? "Upload your receipt first"
                : amountError
                ? "Amount exceeds balance"
                : "I've sent this"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}